<template>
  <b-form 
    ref="form"
    novalidate
    :validated="validated">
    <b-card-title>
      {{ season.seasonId }} Season
      <b-btn
        variant="primary"
        class="float-right"
        :disabled="!season.seasonId"
        @click.prevent="autoGenerateSeason(season)">
        Source From StatsAPI
      </b-btn>
    </b-card-title>
    <b-row>
      <b-col>
        <b-form-group
          label="Stats Season ID"
          label-for="stats-season-id"
          description="The stats-api season id"
          :state="season.seasonId !== null && season.seasonId.length > 0">
          <b-form-input
            v-model="season.seasonId"
            placeholder="Enter the season id associated to stats api"
            required />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Has Wild Card"
          label-for="has-wild-card"
          description="Does this season have a wild card?"
          :state="season.hasWildCard !== null">
          <b-form-checkbox
            v-model="season.hasWildCard"
            inine>
            Wild Card Enabled
          </b-form-checkbox>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          label="Season Start Date"
          label-for="season-start"
          description="The date at the start of the season"
          invalid-feedback="Please select a date"
          :state="season.seasonStartDate !== null && season.seasonStartDate.length > 0">
          <b-form-datepicker
            v-model="season.seasonStartDate"
            :disabled="seasonDatesDisabled"
            required />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Season End Date"
          label-for="season-end"
          description="The date of the end of the season"
          invalid-feedback="Please select a date"
          :state="season.seasonEndDate !== null && season.seasonEndDate.length > 0">
          <b-form-datepicker
            v-model="season.seasonEndDate"
            :disabled="seasonDatesDisabled"
            required />
        </b-form-group>
      </b-col>
    </b-row>
    <hr>
    <b-row>
      <b-col>
        <b-form-group
          label="Show Spring Training"
          label-for="show-spring-training"
          description="Show spring training for this season?"
          :state="season.showSpringTraining !== null">
          <b-form-checkbox
            v-model="season.showSpringTraining"
            inline
            @input="seasonUpdate(season)">
            Spring Training Enabled
          </b-form-checkbox>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row v-if="season.showSpringTraining">
      <b-col>
        <b-form-group
          label="Spring Training Start Date"
          label-for="spring-season-start"
          description="The date of the start of the spring training"
          invalid-feedback="Please select a date"
          :state="season.preSeasonStartDate !== null && season.preSeasonStartDate.length > 0">
          <b-form-datepicker
            v-model="season.preSeasonStartDate"
            required 
            @input="seasonUpdate(season)" />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Spring Training End Date"
          label-for="spring-season-end"
          description="The date of the end of the spring training"
          invalid-feedback="Please select a date"
          :state="season.preSeasonEndDate !== null && season.preSeasonEndDate.length > 0">
          <b-form-datepicker
            v-model="season.preSeasonEndDate"
            required 
            @input="seasonUpdate(season)" />
        </b-form-group>
      </b-col>
    </b-row>
    <hr>
    <b-row>
      <b-col>
        <b-form-group
          label="Regular Season Start Date"
          label-for="regular-season-start"
          description="The date of the start of the regular season"
          invalid-feedback="Please select a date"
          :state="season.regularSeasonStartDate !== null && season.regularSeasonStartDate.length > 0">
          <b-form-datepicker
            v-model="season.regularSeasonStartDate"              
            required
            @input="seasonUpdate(season)" />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Regular Season End Date"
          label-for="regular-season-end"
          description="The date of the end of the regular season"
          invalid-feedback="Please select a date"
          :state="season.regularSeasonEndDate !== null && season.regularSeasonEndDate.length > 0">
          <b-form-datepicker
            v-model="season.regularSeasonEndDate"
            required
            @input="seasonUpdate(season)" />
        </b-form-group>
      </b-col>
    </b-row>
    <hr>
    <b-row>
      <b-col>
        <b-form-group
          label="Show Post Season"
          label-for="show-post-season"
          description="Show post season?"
          :state="season.showPostSeason !== null">
          <b-form-checkbox
            v-model="season.showPostSeason"
            inline
            @input="seasonUpdate(season)">
            Post Season Enabled
          </b-form-checkbox>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row v-if="season.showPostSeason">
      <b-col>
        <b-form-group
          label="Post Season Start Date"
          label-for="post-season-start"
          description="The date of the start of the post season"
          invalid-feedback="Please select a date"
          :state="season.postSeasonStartDate !== null && season.postSeasonStartDate.length > 0">
          <b-form-datepicker
            v-model="season.postSeasonStartDate"
            required
            @input="seasonUpdate(season)" />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Post Season End Date"
          label-for="post-season-end"
          description="The date of the end of the post season"
          invalid-feedback="Please select a date"
          :state="season.postSeasonEndDate !== null && season.postSeasonEndDate.length > 0">
          <b-form-datepicker
            v-model="season.postSeasonEndDate"
            required
            @input="seasonUpdate(season)" />
        </b-form-group>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
  import {mapGetters} from 'vuex'
  import { StatsAPI } from '@/services/api'

  export default {
    name: 'Seasons',
    props: {
      season: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      seasonDatesDisabled: true,
      validated: false,
    }),
    computed: {
      ...mapGetters([
        'isAdmin',
        'canSeasonsEdit',
        'canSeasonsPublish',
      ]),
    },
    methods: {
      isValid() {
        this.validated = true
        return this.$refs.form.checkValidity()
      },
      async seasonUpdate(season) {
        this.$emit("season-update", season)
      },
      async autoGenerateSeason(existing) {
        const seasonData = (await StatsAPI.getSeasons([existing.seasonId])).seasons.find(it => it.seasonId === existing.seasonId)
        if (!seasonData) {
          this.$alert.error(`Unable to retrieve season data for [${existing.seasonId}]`)
          return
        }

        Object.assign(existing, {
          hasWildCard: seasonData.hasWildcard || false,
          postSeasonEndDate: seasonData.postSeasonEndDate || "",
          postSeasonStartDate: seasonData.postSeasonStartDate || "",
          preSeasonEndDate: seasonData.preSeasonEndDate || "",
          preSeasonStartDate: seasonData.preSeasonStartDate || "",
          regularSeasonEndDate: seasonData.regularSeasonEndDate || "",
          regularSeasonStartDate: seasonData.regularSeasonStartDate || "",
          seasonEndDate: seasonData.seasonEndDate || "",
          seasonStartDate: seasonData.seasonStartDate || "",
        })
        this.$alert.success(`Season [${existing.seasonId}] has been updated`)
      },
    },
  }
</script>
